import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { BuscaRemota, CampoControlado, CampoData, Entrada, Rotulo, useCamposPersonalizados, validarCampos, EntradaSensivel, Selecao } from '../../componentes/formulario';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { ErroRequisicao, getTodos, mensagemDeErro, post } from '../../nucleo/api';
import { CHAVE_ETIQUETAS, PRIORIDADES, type Conversa, type Fila, type Prioridade, nomeDoContato } from './tipos';
import { nomeParceiro } from '../../nucleo/formato';

function useAcao<T, R = unknown>(conversa: Conversa, caminho: string, sucesso: string | ((r: R) => string), aoConcluir: () => void, aoFalhar?: (e: unknown) => void) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (corpo?: T) => post<R>(`/conversas/conversas/${conversa.id}/${caminho}`, corpo ?? {}),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['conversas'] });
      if (caminho === 'classificar') void qc.invalidateQueries({ queryKey: CHAVE_ETIQUETAS });
      toast.sucesso(typeof sucesso === 'function' ? sucesso(r) : sucesso);
      aoConcluir();
    },
    onError: (e) => {
      aoFalhar?.(e);
      toast.erro(mensagemDeErro(e));
    },
  });
}

export function TransferirModal({ conversa, aberto, aoFechar }: { conversa: Conversa; aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation('conversas');
  const { t: tn } = useTranslation();
  const [atendente, setAtendente] = useState<string | null>(null);
  // Fase 2 (W9): mover de fila ('' mantém a atual; 'sem' tira de fila).
  const [fila, setFila] = useState('');
  const filas = useQuery({ queryKey: ['conversas', 'filas', 'ativas'], queryFn: () => getTodos<Fila>('/conversas/filas', { ativa: true }), enabled: aberto });
  const filaEscolhida = fila === '' ? undefined : fila === 'sem' ? null : fila;
  const transferir = useAcao<{ atendente_id: string | null; fila_id?: string | null }>(conversa, 'transferir', t('transferir.sucesso'), () => {
    setAtendente(null);
    setFila('');
    aoFechar();
  });
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('transferir.titulo')}
      rodape={
        <>
          {conversa.atendente_id && (
            <Botao variante="perigo" ocupado={transferir.isPending} onClick={() => transferir.mutate({ atendente_id: null, ...(filaEscolhida !== undefined ? { fila_id: filaEscolhida } : {}) })}>
              {t('acoes.devolver')}
            </Botao>
          )}
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" disabled={!atendente && filaEscolhida === undefined} carregando={transferir.isPending} onClick={() => transferir.mutate({ atendente_id: atendente, ...(filaEscolhida !== undefined ? { fila_id: filaEscolhida } : {}) })}>
            {t('acoes.transferir')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-tinta-3">{t('transferir.texto')}</p>
        <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
          {t('transferir.atendente')}
          <BuscaRemota id="transferir-atendente" listaNoFluxo def={{ caminho: '/membros', rotulo: (i) => String(i.nome), pessoa: true }} valor={atendente} aoMudar={(v) => setAtendente(v)} />
        </label>
        {!!filas.data?.itens.length && (
          <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
            {t('transferirFila.fila')}
            <Selecao
              opcoes={[{ valor: '', rotulo: t('transferirFila.manter') }, { valor: 'sem', rotulo: t('transferirFila.semFila') }, ...filas.data.itens.map((f) => ({ valor: f.id, rotulo: f.nome }))]}
              value={fila}
              onChange={(e) => setFila(e.target.value)}
            />
            <span className="text-xs font-normal text-tinta-3">{t('transferirFila.ajuda')}</span>
          </label>
        )}
      </div>
    </Modal>
  );
}

export function EncerrarConfirmar({ conversa, aberto, aoFechar }: { conversa: Conversa; aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation('conversas');
  const encerrar = useAcao<undefined>(conversa, 'encerrar', t('encerrar.sucesso'), aoFechar);
  return <Confirmar aberto={aberto} aoFechar={aoFechar} aoConfirmar={() => encerrar.mutate(undefined)} carregando={encerrar.isPending} titulo={t('encerrar.titulo')} texto={t('encerrar.texto')} perigoso />;
}

export function OportunidadeModal({ conversa, aberto, aoFechar }: { conversa: Conversa; aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation('conversas');
  const { t: tn } = useTranslation();
  const [titulo, setTitulo] = useState('');
  // Ação manual: os campos personalizados obrigatórios da oportunidade são exigidos, como na criação pelo Pipeline.
  // Review r29 spec (B2): só busca os campos com o modal aberto; fechado (montado em toda conversa) dava 403 sem o Pipeline
  // ou sem a permissão dele.
  const extrasDefs = useCamposPersonalizados(aberto ? 'pipeline.oportunidade' : undefined);
  const [extras, setExtras] = useState<Record<string, unknown>>({});
  const [erros, setErros] = useState<Record<string, string>>({});
  // Review r4: uma oportunidade aberta por conversa; a segunda tentativa devolve a mesma (criada: false).
  const criar = useAcao<{ titulo?: string; extras?: Record<string, unknown> }, { oportunidade_id: string; criada: boolean }>(
    conversa,
    'oportunidade',
    (r) => (r.criada === false ? t('oportunidade.jaAberta') : t('oportunidade.sucesso')),
    () => {
      setTitulo('');
      setExtras({});
      setErros({});
      aoFechar();
    },
    (e) => setErros(e instanceof ErroRequisicao ? e.porCampo() : {}),
  );
  const enviar = () => {
    const locais = validarCampos(extrasDefs, { extras });
    setErros(locais);
    if (Object.keys(locais).length) return;
    criar.mutate({ ...(titulo.trim() ? { titulo: titulo.trim() } : {}), ...(extrasDefs.length ? { extras } : {}) });
  };
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('oportunidade.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={criar.isPending} onClick={enviar}>
            {t('acoes.oportunidade')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
          {t('oportunidade.campo')}
          <EntradaSensivel mascara={null} livre value={titulo} maxLength={200} placeholder={conversa.parceiro_nome ? nomeParceiro(conversa.parceiro_nome) : nomeDoContato(conversa)} onChange={(e) => setTitulo(e.target.value)} />
        </label>
        {extrasDefs.map((c) => (
          <CampoControlado key={c.nome} def={c} valor={extras[c.nome.slice(7)]} erro={erros[c.nome] ?? erros[c.nome.slice(7)]} aoMudar={(v) => setExtras((x) => ({ ...x, [c.nome.slice(7)]: v }))} />
        ))}
      </div>
    </Modal>
  );
}

export function AgendarModal({ conversa, aberto, aoFechar }: { conversa: Conversa; aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation('conversas');
  const { t: tn } = useTranslation();
  const [recurso, setRecurso] = useState<string | null>(null);
  const [servico, setServico] = useState<string | null>(null);
  const [inicio, setInicio] = useState('');
  // Review r4 (spec M7): agendar pela conversa é ação manual; os campos obrigatórios do agendamento são exigidos.
  // Review r29 spec (B2): só busca os campos com o modal aberto (fechado dava 403 sem a Agenda ou sem a permissão dela).
  const extrasDefs = useCamposPersonalizados(aberto ? 'agenda.agendamento' : undefined);
  const [extras, setExtras] = useState<Record<string, unknown>>({});
  const [erros, setErros] = useState<Record<string, string>>({});
  const agendar = useAcao<{ recurso_id: string; servico_id: string; inicio: string; extras?: Record<string, unknown> }>(
    conversa,
    'agendamento',
    t('agendar.sucesso'),
    () => {
      setRecurso(null);
      setServico(null);
      setInicio('');
      setExtras({});
      setErros({});
      aoFechar();
    },
    (e) => setErros(e instanceof ErroRequisicao ? e.porCampo() : {}),
  );
  const pronto = !!recurso && !!servico && !!inicio;
  const enviar = () => {
    const locais = validarCampos(extrasDefs, { extras });
    setErros(locais);
    if (Object.keys(locais).length) return;
    agendar.mutate({ recurso_id: recurso!, servico_id: servico!, inicio: new Date(inicio).toISOString(), ...(extrasDefs.length ? { extras } : {}) });
  };
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('agendar.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" disabled={!pronto} carregando={agendar.isPending} onClick={enviar}>
            {t('acoes.agendar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
          {t('agendar.recurso')}
          <BuscaRemota id="agendar-recurso" listaNoFluxo def={{ caminho: '/agenda/recursos', rotulo: (i) => String(i.nome) }} valor={recurso} aoMudar={(v) => setRecurso(v)} />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
          {t('agendar.servico')}
          <BuscaRemota id="agendar-servico" listaNoFluxo def={{ caminho: '/agenda/servicos', rotulo: (i) => String(i.nome) }} valor={servico} aoMudar={(v) => setServico(v)} />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
          {t('agendar.inicio')}
          <CampoData type="datetime-local" value={inicio} onChange={(e) => setInicio(e.target.value)} />
        </label>
        {extrasDefs.map((c) => (
          <CampoControlado key={c.nome} def={c} valor={extras[c.nome.slice(7)]} erro={erros[c.nome] ?? erros[c.nome.slice(7)]} aoMudar={(v) => setExtras((x) => ({ ...x, [c.nome.slice(7)]: v }))} />
        ))}
      </div>
    </Modal>
  );
}

/**
 * Trilha 5b (Digisac): etiquetas (separadas por vírgula) e prioridade da conversa. O servidor normaliza (sem repetir, até
 * 10) e devolve o erro por campo.
 */
export function ClassificarModal({ conversa, aberto, aoFechar }: { conversa: Conversa; aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation('conversas');
  const { t: tn } = useTranslation();
  const [etiquetas, setEtiquetas] = useState((conversa.etiquetas ?? []).join(', '));
  const [prioridade, setPrioridade] = useState<Prioridade>(conversa.prioridade ?? 'normal');
  const [erros, setErros] = useState<Record<string, string>>({});
  const salvar = useAcao<{ etiquetas: string[]; prioridade: Prioridade }>(
    conversa,
    'classificar',
    t('t5b.classificado'),
    () => {
      setErros({});
      aoFechar();
    },
    (e) => setErros(e instanceof ErroRequisicao ? e.porCampo() : {}),
  );
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('t5b.classificarTitulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={salvar.isPending} onClick={() => salvar.mutate({ etiquetas: etiquetas.split(',').map((x) => x.trim()).filter(Boolean), prioridade })}>
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Rotulo id="conversa-etiquetas" rotulo={t('t5b.etiquetas')} ajuda={t('t5b.ajudaEtiquetas')} erro={erros.etiquetas}>
          <Entrada id="conversa-etiquetas" value={etiquetas} maxLength={400} onChange={(e) => setEtiquetas(e.target.value)} />
        </Rotulo>
        <Rotulo id="conversa-prioridade" rotulo={t('t5b.prioridade')} erro={erros.prioridade}>
          <Selecao id="conversa-prioridade" opcoes={PRIORIDADES.map((p) => ({ valor: p, rotulo: t(`t5b.prioridades.${p}`) }))} value={prioridade} onChange={(e) => setPrioridade(e.target.value as Prioridade)} />
        </Rotulo>
      </div>
    </Modal>
  );
}
